"use client";

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import api from "@/app/_module/services/api";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import appointmentQueryKeys from "@/app/(authenticated)/appointments/_modules/services/keys";
import realTimeService from "@/app/_module/services/realTimeService";
import type { RealTimeEvent } from "@/app/_module/services/realTimeService";
import { useNotificationSound, useNotificationSoundPreference } from "@/app/_module/hooks/use-notification-sound";

// --- Types ---

export interface AppointmentReminder {
  appointmentId: string;
  title: string;
  leadId?: string;
  leadName?: string;
  leadPhone?: string;
  leadState?: string;
  appointmentType?: string;
  startTime: string;
  minutesUntil: number;
  urgency: "warning" | "critical";
}

interface DismissedEntry {
  action: "snoozed" | "started" | "dismissed";
  timestamp: number;
  snoozedUntil?: number;
}

interface AppointmentReminderContextType {
  activeReminders: AppointmentReminder[];
  snoozeReminder: (appointmentId: string, minutes?: number) => void;
  startAppointment: (appointmentId: string) => void;
  dismissReminder: (appointmentId: string) => void;
}

const AppointmentReminderContext = createContext<AppointmentReminderContextType | null>(null);

// --- Constants ---

const STORAGE_KEY = "appointment-reminder-state";
const FIFTEEN_MIN_MS = 15 * 60 * 1000;
const FIVE_MIN_MS = 5 * 60 * 1000;
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minute safety poll
const CLEANUP_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

// --- LocalStorage helpers ---

function loadDismissedState(): Map<string, DismissedEntry> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const parsed: Record<string, DismissedEntry> = JSON.parse(raw);
    const map = new Map<string, DismissedEntry>();
    const cutoff = Date.now() - CLEANUP_AGE_MS;
    for (const [key, entry] of Object.entries(parsed)) {
      if (entry.timestamp > cutoff) {
        map.set(key, entry);
      }
    }
    return map;
  } catch {
    return new Map();
  }
}

function saveDismissedState(map: Map<string, DismissedEntry>) {
  try {
    const obj: Record<string, DismissedEntry> = {};
    map.forEach((val, key) => { obj[key] = val; });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    // Ignore storage errors
  }
}

// --- Helper to extract lead info from appointment ---

function extractLeadInfo(appointment: any): {
  leadId?: string;
  leadName?: string;
  leadPhone?: string;
  leadState?: string;
} {
  const result: { leadId?: string; leadName?: string; leadPhone?: string; leadState?: string } = {};

  // Direct leadId on appointment (check both camelCase and snake_case)
  if (appointment.leadId || appointment.lead_id) {
    result.leadId = appointment.leadId || appointment.lead_id;
  }

  // Check associations for lead info
  if (appointment.associations && Array.isArray(appointment.associations)) {
    for (const assoc of appointment.associations) {
      if (assoc.entityType?.toLowerCase() === "lead") {
        result.leadId = result.leadId || assoc.entityId;
        result.leadName = assoc.entityName;
        // Check metadata for additional lead info
        if (assoc.metadata) {
          result.leadState = assoc.metadata.leadState;
          result.leadPhone = assoc.metadata.leadPhone;
        }
        break;
      }
    }
  }

  return result;
}

// --- Provider ---

export function AppointmentReminderProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [activeReminders, setActiveReminders] = useState<AppointmentReminder[]>([]);
  const leadIdMapRef = useRef<Map<string, string>>(new Map()); // appointmentId → leadId (avoids stale closures)
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>[]>>(new Map());
  const dismissedRef = useRef<Map<string, DismissedEntry>>(loadDismissedState());

  // Notification sound - uses same ping as the notification bell
  const { getSoundPreference } = useNotificationSoundPreference();
  const [soundEnabled, setSoundEnabled] = React.useState(getSoundPreference());
  const { playSound } = useNotificationSound({ enabled: soundEnabled });

  React.useEffect(() => {
    const handlePreferenceChange = (event: CustomEvent) => {
      setSoundEnabled(event.detail.enabled);
    };
    window.addEventListener('notificationSoundPreferenceChanged', handlePreferenceChange as EventListener);
    return () => {
      window.removeEventListener('notificationSoundPreferenceChanged', handlePreferenceChange as EventListener);
    };
  }, []);

  // Fetch today's + tomorrow's appointments
  // Only poll for a signed-in user. This provider mounts with the app shell,
  // which renders for a beat before an anonymous visitor is bounced to sign-in
  // — long enough to fire an authenticated call with no token. Prod was
  // taking a steady stream of 401s on /appointments/today from crawlers and
  // scanners loading the shell (2026-09-04).
  const { user: reminderUser } = useUserContext();
  const reminderUserId: string | undefined = (reminderUser as any)?.user?.id;

  const { data: appointmentsData, refetch } = useQuery({
    queryKey: appointmentQueryKeys.today(true),
    enabled: !!reminderUserId,
    queryFn: async () => {
      const baseUrl = getApiUrl(endpoint.appointments.getTodaysAppointments);
      const response = await api.get({
        url: `${baseUrl}?includeTomorrow=true`,
        auth: true,
      });
      // Axios interceptor unwraps response.data, so we get the backend body directly
      const body = response as any;
      return body?.data || body?.items || [];
    },
    staleTime: 60 * 1000, // 1 minute
    gcTime: 10 * 60 * 1000,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  // Build a reminder object and cache leadId in ref for reliable navigation
  const buildReminder = useCallback(
    (appointment: any, minutesUntil: number, urgency: "warning" | "critical"): AppointmentReminder => {
      const leadInfo = extractLeadInfo(appointment);
      // Cache leadId in ref so startAppointment can always find it (avoids stale closure)
      if (leadInfo.leadId) {
        leadIdMapRef.current.set(appointment.id, leadInfo.leadId);
      }
      return {
        appointmentId: appointment.id,
        title: appointment.title || "Appointment",
        leadId: leadInfo.leadId,
        leadName: leadInfo.leadName,
        leadPhone: leadInfo.leadPhone,
        leadState: leadInfo.leadState,
        appointmentType: appointment.type,
        startTime: appointment.startTime,
        minutesUntil,
        urgency,
      };
    },
    []
  );

  // Add a reminder to active list (deduplicating by appointmentId+urgency)
  const addReminder = useCallback(
    (appointment: any, minutesUntil: number, urgency: "warning" | "critical") => {
      const key = `${appointment.id}-${urgency}`;
      // Check if snoozed
      const dismissed = dismissedRef.current.get(key);
      if (dismissed) {
        if (dismissed.action === "started" || dismissed.action === "dismissed") return;
        if (dismissed.action === "snoozed" && dismissed.snoozedUntil && Date.now() < dismissed.snoozedUntil) return;
      }

      const reminder = buildReminder(appointment, minutesUntil, urgency);
      setActiveReminders((prev) => {
        // Remove any existing reminder for same appointment (critical upgrades warning)
        const filtered = prev.filter((r) => r.appointmentId !== appointment.id);
        return [...filtered, reminder];
      });

      // Play notification sound
      playSound();
    },
    [buildReminder, playSound]
  );

  // Setup timers for all appointments
  const setupTimers = useCallback(
    (appointments: any[]) => {
      // Clear existing timers
      timersRef.current.forEach((timers) => timers.forEach(clearTimeout));
      timersRef.current.clear();

      const now = Date.now();
      const immediateReminders: AppointmentReminder[] = [];

      for (const apt of appointments) {
        if (!apt.startTime) continue;
        const startMs = new Date(apt.startTime).getTime();
        const msUntil = startMs - now;

        // Skip if appointment already started
        if (msUntil <= 0) continue;

        // Skip cancelled/completed appointments
        const status = apt.status?.toLowerCase();
        if (status === "cancelled" || status === "completed" || status === "no_show") continue;

        const timers: ReturnType<typeof setTimeout>[] = [];

        // --- 15-minute reminder ---
        const ms15 = msUntil - FIFTEEN_MIN_MS;
        const key15 = `${apt.id}-warning`;
        const dismissed15 = dismissedRef.current.get(key15);
        const show15 =
          !dismissed15 ||
          (dismissed15.action === "snoozed" && dismissed15.snoozedUntil && Date.now() >= dismissed15.snoozedUntil);

        if (show15) {
          if (ms15 > 0) {
            // Schedule for future
            timers.push(setTimeout(() => addReminder(apt, 15, "warning"), ms15));
          } else if (msUntil > FIVE_MIN_MS) {
            // Should already be showing (between 5-15 min window)
            immediateReminders.push(buildReminder(apt, Math.round(msUntil / 60000), "warning"));
          }
        }

        // --- 5-minute reminder ---
        const ms5 = msUntil - FIVE_MIN_MS;
        const key5 = `${apt.id}-critical`;
        const dismissed5 = dismissedRef.current.get(key5);
        const show5 =
          !dismissed5 ||
          (dismissed5.action === "snoozed" && dismissed5.snoozedUntil && Date.now() >= dismissed5.snoozedUntil);

        if (show5) {
          if (ms5 > 0) {
            // Schedule for future
            timers.push(setTimeout(() => addReminder(apt, 5, "critical"), ms5));
          } else if (msUntil > 0) {
            // Should already be showing (< 5 min window)
            immediateReminders.push(buildReminder(apt, Math.round(msUntil / 60000), "critical"));
          }
        }

        if (timers.length > 0) {
          timersRef.current.set(apt.id, timers);
        }
      }

      // Set immediate reminders
      if (immediateReminders.length > 0) {
        setActiveReminders((prev) => {
          const newList = [...prev];
          let hasNew = false;
          for (const reminder of immediateReminders) {
            const exists = newList.some(
              (r) => r.appointmentId === reminder.appointmentId && r.urgency === reminder.urgency
            );
            if (!exists) {
              newList.push(reminder);
              hasNew = true;
            }
          }
          if (hasNew) playSound();
          return newList;
        });
      }
    },
    [addReminder, buildReminder, playSound]
  );

  // Recalculate timers when appointment data changes
  useEffect(() => {
    const appointments = Array.isArray(appointmentsData) ? appointmentsData : [];
    if (appointments.length > 0) {
      setupTimers(appointments);
    }
    return () => {
      timersRef.current.forEach((timers) => timers.forEach(clearTimeout));
      timersRef.current.clear();
    };
  }, [appointmentsData, setupTimers]);

  // Handle visibility change - recalculate when tab becomes active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const appointments = Array.isArray(appointmentsData) ? appointmentsData : [];
        if (appointments.length > 0) {
          setupTimers(appointments);
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [appointmentsData, setupTimers]);

  // Subscribe to real-time appointment events
  useEffect(() => {
    const handleAppointmentEvent = (_event: RealTimeEvent) => {
      // Refetch today's appointments to recalculate timers
      refetch();
      // Also invalidate other appointment queries
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
    };

    realTimeService.addEventListener("appointments:created", handleAppointmentEvent);
    realTimeService.addEventListener("appointments:updated", handleAppointmentEvent);
    realTimeService.addEventListener("appointments:deleted", handleAppointmentEvent);

    return () => {
      realTimeService.removeEventListener("appointments:created", handleAppointmentEvent);
      realTimeService.removeEventListener("appointments:updated", handleAppointmentEvent);
      realTimeService.removeEventListener("appointments:deleted", handleAppointmentEvent);
    };
  }, [refetch, queryClient]);

  // --- Actions ---

  const snoozeReminder = useCallback((appointmentId: string, minutes = 5) => {
    // Remove from active reminders
    setActiveReminders((prev) => prev.filter((r) => r.appointmentId !== appointmentId));

    // Save to dismissed state
    const snoozedUntil = Date.now() + minutes * 60 * 1000;
    dismissedRef.current.set(`${appointmentId}-warning`, {
      action: "snoozed",
      timestamp: Date.now(),
      snoozedUntil,
    });
    dismissedRef.current.set(`${appointmentId}-critical`, {
      action: "snoozed",
      timestamp: Date.now(),
      snoozedUntil,
    });
    saveDismissedState(dismissedRef.current);

    // Set timer to re-check after snooze expires
    setTimeout(() => {
      const appointments = Array.isArray(appointmentsData) ? appointmentsData : [];
      if (appointments.length > 0) {
        setupTimers(appointments);
      }
    }, minutes * 60 * 1000);
  }, [appointmentsData, setupTimers]);

  const startAppointment = useCallback(
    (appointmentId: string) => {
      // Get leadId from ref (immune to stale closures)
      const leadId = leadIdMapRef.current.get(appointmentId);

      // Remove from active reminders
      setActiveReminders((prev) => prev.filter((r) => r.appointmentId !== appointmentId));

      // Mark as started
      dismissedRef.current.set(`${appointmentId}-warning`, {
        action: "started",
        timestamp: Date.now(),
      });
      dismissedRef.current.set(`${appointmentId}-critical`, {
        action: "started",
        timestamp: Date.now(),
      });
      saveDismissedState(dismissedRef.current);

      // Navigate to lead details, or fallback to appointments page
      // (inside the native shell every screen lives under /m)
      const shellPrefix = typeof window !== "undefined" && window.location.pathname.startsWith("/m") ? "/m" : "";
      if (leadId) {
        router.push(`${shellPrefix}/leads/${leadId}`);
      } else {
        router.push(`${shellPrefix}/appointments`);
      }
    },
    [router]
  );

  const dismissReminder = useCallback((appointmentId: string) => {
    setActiveReminders((prev) => prev.filter((r) => r.appointmentId !== appointmentId));

    dismissedRef.current.set(`${appointmentId}-warning`, {
      action: "dismissed",
      timestamp: Date.now(),
    });
    dismissedRef.current.set(`${appointmentId}-critical`, {
      action: "dismissed",
      timestamp: Date.now(),
    });
    saveDismissedState(dismissedRef.current);
  }, []);

  const value: AppointmentReminderContextType = {
    activeReminders,
    snoozeReminder,
    startAppointment,
    dismissReminder,
  };

  return (
    <AppointmentReminderContext.Provider value={value}>
      {children}
    </AppointmentReminderContext.Provider>
  );
}

export function useAppointmentReminders(): AppointmentReminderContextType {
  const context = useContext(AppointmentReminderContext);
  if (!context) {
    throw new Error("useAppointmentReminders must be used within AppointmentReminderProvider");
  }
  return context;
}
